/**
 * SOLID Review
 * S — Good. One concern: the account's shells — signing in by code, signing up, the referral check, the
 *     profile, signing out — each as plan → one server call → answer, on the shared serial section. No
 *     rule lives here; the Domain's decisions hold them all.
 * O — Good. A new auth step is one more shell of the same shape and one plan/answer pair.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Exactly the account's ops of `ClientUseCases`, plus the sign-out the session's entry shares.
 * D — Strong. Two port interfaces and the shared steps, all injected; no adapter, no store, no React.
 */
import type { IPortClientDecisions } from '../../domain/ports/ClientDecisions.contract';
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { ICommandNothing } from '../../domain/ports/schemas/TableMoves';
import type {
  ICommandEmail, ICommandSignUp, ICommandVerifyCode, ICommandReferral, ICommandNickname, ICommandEmailCode,
  ICommandLoginCodeAnswered, ICommandSignedUp, ICommandSignedIn, ICommandProfileLoaded, ICommandEmailChanged,
  IEntityLoginCode, IPayloadReferral, IPayloadEmail, IPayloadProfile,
} from '../../domain/ports/schemas/AuthMoves';
import { SERVER_REFUSED, type IPortClientAuthServer } from '../ports/contracts';
import { outcomeOf, type ClientSteps, type TOutcome, type TResult } from '../shared/ClientSteps';

export interface IConfigDepAuthContext {
  readonly decisions: IPortClientDecisions;
  readonly auth: IPortClientAuthServer;
  /** The one serial section, shared with the table and the session. */
  readonly steps: ClientSteps;
}

/** Signing the tab out — what the session's entry asks for when no player is behind the token. */
export interface IServiceSignOut {
  signOut(ctx: IContextRequest, command: ICommandNothing): Promise<TOutcome>;
}

const NOTHING: ICommandNothing = {};

/**
 * THE ACCOUNT'S SHELLS (frontend strict-ports plan, FE3, 2026-10-03). LoginScreen, VerifyCodeScreen,
 * ProfileModal and the welcome screen's logout called the auth REST adapter with the JWT they read from
 * the auth store, and wrote the store around each call. Each is a shell of FE1's pattern now: a step plans
 * (the form's checks), the server is called between two steps, a step answers (what the screen wrote).
 *
 * The token is never here. `ClientAuthServer` keeps it: a code accepted answers who signed in, and the
 * profile's calls send the token they hold.
 */
export class AuthContext implements IServiceSignOut {
  constructor(private readonly deps: IConfigDepAuthContext) {}

  /**
   * The email screen in sign-in mode. The server's refusal reaches the decision, which turns "no account"
   * into the sign-up form in place; a server that could not be asked is not the decision's to word.
   */
  async requestLoginCode(ctx: IContextRequest, command: ICommandEmail): Promise<TResult<IEntityLoginCode>> {
    const { decisions, steps, auth } = this.deps;
    const plan = await steps.session(ctx, (input) => decisions.planLoginCode(ctx, input, command));
    if (!plan.success) return plan;
    const sent = await auth.requestLoginCode(ctx, plan.value);
    if (!sent.success && sent.error.code !== SERVER_REFUSED) return sent;
    const answered: ICommandLoginCodeAnswered = { email: plan.value.email, refusal: sent.success ? null : sent.error.message };
    return steps.session(ctx, (input) => decisions.loginCodeAnswered(ctx, input, answered));
  }

  /** The sign-up form: register, then on to the code screen. */
  async signUp(ctx: IContextRequest, command: ICommandSignUp): Promise<TOutcome> {
    const { decisions, steps, auth } = this.deps;
    const plan = await steps.session(ctx, (input) => decisions.planSignUp(ctx, input, command));
    if (!plan.success) return plan;
    const registered = await auth.register(ctx, plan.value);
    if (!registered.success) return registered;
    const signedUp: ICommandSignedUp = { email: plan.value.email };
    return outcomeOf(await steps.session(ctx, (input) => decisions.signedUp(ctx, input, signedUp)));
  }

  /** The code screen: a code accepted signs the tab in. */
  async verifyCode(ctx: IContextRequest, command: ICommandVerifyCode): Promise<TOutcome> {
    const { decisions, steps, auth } = this.deps;
    const plan = await steps.session(ctx, (input) => decisions.planVerifyCode(ctx, input, command));
    if (!plan.success) return plan;
    const verified = await auth.verifyCode(ctx, plan.value);
    if (!verified.success) return verified;
    const signedIn: ICommandSignedIn = { identity: verified.value };
    return outcomeOf(await steps.session(ctx, (input) => decisions.signedIn(ctx, input, signedIn)));
  }

  /** The referral field's check on blur: a read with no rule and nothing to keep. */
  async checkReferral(ctx: IContextRequest, command: ICommandReferral): Promise<TResult<IPayloadReferral>> {
    return this.deps.auth.validateReferral(ctx, command);
  }

  /**
   * The profile opened: the account as the server has it, kept and answered (the modal fills its nickname
   * field from it). A failed read keeps what the store holds.
   */
  async loadProfile(ctx: IContextRequest, _command: ICommandNothing): Promise<TResult<IPayloadProfile | null>> {
    const { decisions, steps, auth } = this.deps;
    const me = await auth.me(ctx);
    if (!me.success) return me;
    if (me.value === null) return { success: true, value: null };
    const { displayName, email, matchCredits } = me.value;
    const profile: IPayloadProfile = { displayName, email, matchCredits };
    const loaded: ICommandProfileLoaded = { profile };
    const kept = await steps.session(ctx, (input) => decisions.profileLoaded(ctx, input, loaded));
    if (!kept.success) return kept;
    return { success: true, value: profile };
  }

  async changeNickname(ctx: IContextRequest, command: ICommandNickname): Promise<TOutcome> {
    const { decisions, steps, auth } = this.deps;
    const plan = await steps.session(ctx, (input) => decisions.planNickname(ctx, input, command));
    if (!plan.success) return plan;
    const saved = await auth.updateDisplayName(ctx, plan.value);
    if (!saved.success) return saved;
    const changed: ICommandNickname = { displayName: saved.value.displayName };
    return outcomeOf(await steps.session(ctx, (input) => decisions.nicknameChanged(ctx, input, changed)));
  }

  /** The profile's "send code": answers the address the code went to, for the screen's line. */
  async requestEmailChange(ctx: IContextRequest, command: ICommandEmail): Promise<TResult<IPayloadEmail>> {
    const { decisions, steps, auth } = this.deps;
    const plan = await steps.session(ctx, (input) => decisions.planEmailChange(ctx, input, command));
    if (!plan.success) return plan;
    const sent = await auth.requestEmailChange(ctx, plan.value);
    if (!sent.success) return sent;
    return { success: true, value: plan.value };
  }

  async verifyEmailChange(ctx: IContextRequest, command: ICommandEmailCode): Promise<TOutcome> {
    const { decisions, steps, auth } = this.deps;
    const plan = await steps.session(ctx, (input) => decisions.planVerifyEmailChange(ctx, input, command));
    if (!plan.success) return plan;
    const saved = await auth.verifyEmailChange(ctx, plan.value);
    if (!saved.success) return saved;
    const changed: ICommandEmailChanged = { email: saved.value.email };
    return outcomeOf(await steps.session(ctx, (input) => decisions.emailChanged(ctx, input, changed)));
  }

  /**
   * Signing out (the welcome screen's and the profile's Log out; the entry, when no player is behind the
   * token): the client forgets the account in one step, then the token is forgotten.
   */
  async signOut(ctx: IContextRequest, _command: ICommandNothing): Promise<TOutcome> {
    const { decisions, steps, auth } = this.deps;
    const out = await steps.session(ctx, (input) => decisions.signedOut(ctx, input, NOTHING));
    const forgotten = await auth.signOut(ctx);
    if (!out.success) return out;
    return outcomeOf(forgotten);
  }
}
